import { useState, useCallback } from 'react';

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'warning' | 'error';
  duration: number;
  action?: NotificationAction;
}

export interface NotificationService {
  notifications: Notification[];
  show: (message: string, type?: 'success' | 'warning' | 'error', duration?: number, action?: NotificationAction) => void;
  success: (message: string, duration?: number, action?: NotificationAction) => void;
  warning: (message: string, duration?: number, action?: NotificationAction) => void;
  error: (message: string, duration?: number, action?: NotificationAction) => void;
  remove: (id: string) => void;
}

export const useNotificationService = (): NotificationService => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const show = useCallback((message: string, type: 'success' | 'warning' | 'error' = 'success', duration: number = 5000, action?: NotificationAction) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification: Notification = {
      id,
      message,
      type,
      duration,
      action
    };
    setNotifications(prev => {
      const newNotifications = [...prev, notification];
      return newNotifications;
    });
  }, []);

  const success = useCallback((message: string, duration: number = 5000, action?: NotificationAction) => {
    show(message, 'success', duration, action);
  }, [show]);

  const warning = useCallback((message: string, duration: number = 5000, action?: NotificationAction) => {
    show(message, 'warning', duration, action);
  }, [show]);

  const error = useCallback((message: string, duration: number = 5000, action?: NotificationAction) => {
    show(message, 'error', duration, action);
  }, [show]);

  const remove = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  return {
    notifications,
    show,
    success,
    warning,
    error,
    remove
  };
};
